import React, { useEffect, useState } from 'react';
import { SymbolController } from 'pxc-editor-data/Symbol';
import { getAssetsFullPath } from '@/helpers';
import { checkSpecialSymbol } from 'pxc-editor-web/src/helpers';
import { getImageCoordinates } from './imageLayout';

const convertToBase64 = async ({ src, glyphFillColor }) => {
  const svgContent = await loadFileContent(src);
  const svgString = svgContent.replace(
    /<path([^>]*)>/,
    `<path$1 fill="${glyphFillColor}">`,
  );
  const base64Draft = btoa(unescape(encodeURIComponent(svgString)));
  return `data:image/svg+xml;base64,${base64Draft}`;
};

const loadFileContent = async (src) => {
  return loadFileOnWeb(src);
};

const loadFileOnWeb = async (src) => {
  const response = await fetch(src);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.text();
};

const Glyph = (props) => {
  const { id, rectProps, formatProps } = props;
  const {
    symbolFont,
    glyph,
    glyphFillColor,
    graphicFormattingScale,
  } = formatProps;
  const scaleFactor = checkSpecialSymbol(formatProps) ? 1 : graphicFormattingScale;
  const fileURL = SymbolController.getSymbolUrl({
    symbolFont,
    unicode: glyph.unicode,
  });
  const rectFactor = parseFloat(
    (rectProps.width / rectProps.height).toFixed(2),
  );
  const [imageFactor, setImageFactor] = useState(1);
  const [coordinates, setCoordinates] = useState({ x: 0, y: 0 });
  const [base64Image, setBase64Image] = useState(null);

  const handleImageFactor = (image) => {
    if (image.width || image.height) {
      setImageFactor(parseFloat((image.width / image.height).toFixed(2)));
    } else {
      setImageFactor(1);
    }
  };

  const handleBase64Conversion = async (src) => {
    const base64 = await convertToBase64({ src, glyphFillColor });
    setBase64Image(base64);
  };

  const handleImageLoad = () => {
    const src = getAssetsFullPath(fileURL);
    const img = new Image();

    img.src = src;
    img.onload = function () {
      handleImageFactor(img);
      handleBase64Conversion(src);
    };
  };

  const handleCoordinatesChange = () => {
    setCoordinates(
      getImageCoordinates({
        rectProps,
        rectFactor,
        imageFactor,
        scaleFactor,
        horizontalAlignment: glyph.horizontalAlignment,
        verticalAlignment: glyph.verticalAlignment,
      }),
    );
  };

  useEffect(() => {
    handleImageLoad();
  }, [symbolFont, glyph.unicode, glyphFillColor]);

  useEffect(() => {
    handleCoordinatesChange();
  }, [
    imageFactor,
    rectProps.x,
    rectProps.y,
    rectProps.width,
    rectProps.height,
    glyph.horizontalAlignment,
    glyph.verticalAlignment,
    scaleFactor
  ]);

  const width = rectFactor > imageFactor ? undefined : rectProps.width * scaleFactor;
  const height = rectFactor > imageFactor ? rectProps.height * scaleFactor : undefined;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={rectProps.width}
      height={rectProps.height}
      x={rectProps.x}
      y={rectProps.y}
    >
      <image
        id={id}
        href={base64Image}
        width={width}
        height={height}
        x={coordinates.x}
        y={coordinates.y}
        data-unicode={glyph.unicode}
      />
    </svg>
  );
};

export default Glyph;
